import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Copy, Loader2, Plus, ShieldAlert, Sparkles } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { Skeleton } from "../components/ui/skeleton";
import { API, errMsg } from "../lib/api";
import { DIMENSIONS, SCENARIOS, ROLES, LADDER, gapTone, signed } from "../lib/bopta";
import { BoptaRadar } from "../components/Charts";

const MANAGER = "#1B5C58";
const STAFF = "#FE5201";
const TICK = { fontSize: 11 };
const DOMAIN = [0, 100];
const LEGEND = { fontSize: 12 };
const RADIUS = [4, 4, 0, 0];
const BAR_MARGIN = { top: 20, right: 10, left: -20, bottom: 0 };
const LINE_MARGIN = { top: 20, right: 20, left: -20, bottom: 0 };
const teamMeta = (t) => [t.org, t.manager_name && `Manager: ${t.manager_name}`].filter(Boolean).join(" · ");
const fmt = (v) => (v == null ? "—" : Math.round(v));

const Invites = ({ code, admin }) => {
  const base = window.location.origin;
  const links = [["Staff invite", `${base}/assessment?team=${code}`, "invite-staff"], ["Manager self-assessment", `${base}/assessment?team=${code}&mode=manager`, "invite-manager"], ["Private admin link (bookmark it)", `${base}/team/${code}?admin=${admin}`, "invite-admin"]];
  return (
    <div className="grid gap-3 md:grid-cols-3">
      {links.map(([t, u, id]) => (
        <div key={id} className="panel p-4" data-testid={id}>
          <p className="kicker">{t}</p>
          <p className="mt-2 truncate text-xs muted" title={u}>{u}</p>
          <button className="btn btn-line mt-3 min-h-0 px-3 py-1.5 text-xs" onClick={() => navigator.clipboard.writeText(u).then(() => toast.success("Link copied"))} data-testid={`${id}-copy`}><Copy className="h-3.5 w-3.5" /> Copy</button>
        </div>
      ))}
    </div>
  );
};

const Stat = ({ label, value, testid, color }) => (
  <div className="panel p-5" data-testid={testid}><p className="kicker">{label}</p><p className="display mt-2 text-4xl font-extrabold" style={{ color }}>{value ?? "—"}</p></div>
);

const Charts = ({ d }) => {
  const series = [d.staff_avg && { name: "Staff average", dims: d.staff_avg, color: STAFF }, d.manager_dims && { name: "Manager", dims: d.manager_dims, color: MANAGER }].filter(Boolean);
  const bars = DIMENSIONS.map((x) => ({ dim: x.name, Manager: d.manager_dims?.[x.k] ?? 0, Staff: d.staff_avg?.[x.k] ?? 0 }));
  return (
    <section className="mt-6 grid gap-5 lg:grid-cols-2">
      <div className="panel p-5"><p className="kicker">BOPTA overlay</p><BoptaRadar series={series} height={320} /></div>
      <div className="panel p-5" data-testid="gap-chart"><p className="kicker">Perception gap by dimension</p>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={bars} margin={BAR_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4DE" vertical={false} />
            <XAxis dataKey="dim" tick={TICK} /><YAxis domain={DOMAIN} tick={TICK} />
            <Tooltip /><Legend wrapperStyle={LEGEND} />
            <Bar dataKey="Manager" fill={MANAGER} radius={RADIUS} /><Bar dataKey="Staff" fill={STAFF} radius={RADIUS} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
};

const Matrix = ({ d }) => (
  <section className="panel mt-5 overflow-x-auto p-5" data-testid="team-matrix">
    <table className="w-full text-sm">
      <thead><tr className="text-left muted"><th className="py-2 font-normal">Dimension</th><th className="font-normal">Staff avg</th><th className="font-normal">Range</th><th className="font-normal">Manager</th><th className="font-normal">Gap</th></tr></thead>
      <tbody>
        {DIMENSIONS.map((x) => {
          const g = d.gaps?.[x.k];
          return (
            <tr key={x.k} className="border-t border-[var(--line)]" data-testid={`matrix-row-${x.k}`}>
              <td className="py-3"><span className="chip mr-2" style={{ background: x.color }}>{x.k}</span>{x.name}</td>
              <td>{fmt(d.staff_avg?.[x.k])}</td>
              <td className="muted">{d.spread ? `${Math.round(d.spread[x.k][0])}–${Math.round(d.spread[x.k][1])}` : "—"}</td>
              <td>{fmt(d.manager_dims?.[x.k])}</td>
              <td className="font-bold" style={{ color: gapTone(g) }}>{g == null ? "—" : signed(g)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </section>
);

const Mix = ({ d }) => (
  <section className="mt-5 grid gap-5 lg:grid-cols-2">
    <div className="panel p-5" data-testid="team-scenarios"><p className="kicker mb-2">Scenarios across staff</p>
      {["A", "B", "C", "D", "E"].map((k) => [k, SCENARIOS[k]]).map(([k, s]) => <p key={k} className="flex justify-between border-t border-[var(--line)] py-2 text-sm"><span>{k} · {s.name}</span><b>{d.scenario_counts[k]}</b></p>)}
    </div>
    <div className="panel p-5 lg:col-span-2" data-testid="team-level-mix"><p className="kicker mb-2">Staff delegation level mix</p>
      <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {LADDER.map((l) => <div key={l.n} className="rounded-lg p-3" style={{ background: d.manager_level === l.n ? "var(--tint)" : "#F4F4F0" }} data-testid={`level-count-${l.n}`}><p className="text-xs muted">L{l.n} · {l.translation}</p><p className="display text-2xl font-extrabold">{d.level_counts?.[l.n] ?? 0}</p>{d.manager_level === l.n && <p className="text-[11px] font-semibold text-[var(--teal)]">Manager: {l.persona}</p>}</div>)}
      </div>
      {d.manager_level && <p className="mt-3 text-sm" data-testid="manager-level">Manager self-score sits at <b>L{d.manager_level} · {LADDER[d.manager_level - 1].translation}</b>, delegation style <b>{LADDER[d.manager_level - 1].persona}</b>.</p>}
    </div>
    <div className="panel p-5"><p className="kicker mb-2">Role mix</p>
      {ROLES.map((r) => <p key={r.v} className="flex justify-between border-t border-[var(--line)] py-2 text-sm"><span>{r.l}</span><b>{d.role_counts[r.v]}</b></p>)}
    </div>
  </section>
);

const Rounds = ({ d, code, admin, onChange }) => {
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      const r = await API.post(`/teams/${code}/rounds`, null, { params: { admin } });
      toast.success(`Round ${r.data.round} started. Share the same links again.`);
      onChange(r.data.round);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="rounds-bar">
      <span className="kicker mr-2">Rounds</span>
      {d.history.map((h) => (
        <button key={h.round} onClick={() => onChange(h.round)} data-testid={`round-tab-${h.round}`}
          className={`rounded-full px-3 py-1 text-xs font-semibold ${h.round === d.round ? "bg-[var(--ink)] text-white" : "bg-[#EFEFEA] muted"}`}>
          Round {h.round}{h.round === d.current_round ? " · current" : ""}
        </button>
      ))}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button className="btn btn-line ml-auto min-h-0 py-2 text-xs" disabled={busy} data-testid="start-round-btn"><Plus className="h-3.5 w-3.5" /> Start round {d.current_round + 1}</button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start round {d.current_round + 1}?</AlertDialogTitle>
            <AlertDialogDescription>New responses from the same invite links will count toward the new round. Earlier rounds stay saved, so you can compare them.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="start-round-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={start} data-testid="start-round-confirm">Start round</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Trend = ({ history }) => {
  if (history.length < 2) return null;
  const data = history.map((h) => ({ name: `R${h.round}`, "Staff autonomy": h.staff_overall, "Manager self-score": h.manager_overall, "Mental bandwidth": h.bandwidth_avg }));
  return (
    <section className="panel mt-6 p-5" data-testid="round-trend">
      <p className="kicker">Change across rounds</p>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data} margin={LINE_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E4E4DE" vertical={false} />
          <XAxis dataKey="name" tick={TICK} /><YAxis domain={DOMAIN} tick={TICK} />
          <Tooltip /><Legend wrapperStyle={LEGEND} />
          <Line dataKey="Staff autonomy" stroke={STAFF} strokeWidth={2} connectNulls />
          <Line dataKey="Manager self-score" stroke={MANAGER} strokeWidth={2} connectNulls />
          <Line dataKey="Mental bandwidth" stroke="#8A8F8C" strokeWidth={2} strokeDasharray="5 4" connectNulls />
        </LineChart>
      </ResponsiveContainer>
      <table className="mt-3 w-full text-sm">
        <thead><tr className="text-left muted"><th className="py-1 font-normal">Round</th><th className="font-normal">Staff</th><th className="font-normal">Autonomy</th><th className="font-normal">Manager</th><th className="font-normal">Gap</th><th className="font-normal">Bandwidth</th></tr></thead>
        <tbody>{history.map((h) => <tr key={h.round} className="border-t border-[var(--line)]" data-testid={`round-row-${h.round}`}><td className="py-2">Round {h.round}</td><td>{h.staff_count}</td><td>{fmt(h.staff_overall)}</td><td>{fmt(h.manager_overall)}</td><td style={{ color: gapTone(h.overall_gap) }}>{h.overall_gap == null ? "—" : signed(h.overall_gap)}</td><td>{fmt(h.bandwidth_avg)}</td></tr>)}</tbody>
      </table>
    </section>
  );
};

const Insights = ({ code, admin, initial, disabled, round }) => {
  const [ins, setIns] = useState(initial);
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try {
      const r = await API.post(`/teams/${code}/insights`, null, { params: { admin, round } });
      setIns(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <section className="mt-12" data-testid="team-insights">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><p className="kicker">AI team action plan</p><h2 className="mt-2 text-3xl font-bold">From gap to intervention.</h2></div>
        <button className="btn btn-main" disabled={busy || disabled} onClick={gen} data-testid="team-insights-generate">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {ins ? "Refresh plan" : "Generate team plan"}</button>
      </div>
      {disabled && <p className="mt-2 text-sm muted">Available once at least one staff member has responded.</p>}
      {ins && (
        <div className="mt-6 space-y-5">
          <div className="panel p-7"><p className="text-xl font-bold" data-testid="team-insights-headline">{ins.headline}</p><p className="mt-2 muted">{ins.diagnosis}</p><p className="mt-3 text-sm"><b>Priority:</b> {ins.priority_dimension}</p></div>
          <div className="grid gap-4 md:grid-cols-2">
            {(ins.interventions || []).map((x) => <div key={x.title} className="panel p-5"><p className="font-bold">{x.title} <span className="ml-2 rounded bg-[#EFEFEA] px-2 py-0.5 text-[10px] uppercase">{x.owner}</span></p><p className="mt-2 text-sm muted">{x.detail}</p></div>)}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="panel p-5"><p className="kicker">Talent positioning</p><ul className="mt-2 space-y-1 text-sm muted">{(ins.talent_positioning || []).map((t) => <li key={t}>→ {t}</li>)}</ul></div>
            <div className="panel p-5"><p className="kicker">Calibration workshop</p><ol className="mt-2 list-decimal space-y-1 pl-5 text-sm muted">{(ins.workshop_agenda || []).map((t) => <li key={t}>{t}</li>)}</ol></div>
          </div>
        </div>
      )}
    </section>
  );
};

export default function TeamDashboard() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const admin = params.get("admin") || "";
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [round, setRound] = useState(null);
  useEffect(() => {
    API.get(`/teams/${code}/dashboard`, { params: round ? { admin, round } : { admin } }).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  }, [code, admin, round]);

  if (err) return <div className="wrap py-24 text-center" data-testid="team-error"><ShieldAlert className="mx-auto h-10 w-10 text-[var(--orange)]" /><h1 className="mt-3 text-3xl font-bold">{err}</h1><p className="mt-2 muted">Use the private admin link from when you created the team.</p><Link to="/employers" className="btn btn-main mt-6">Back to employers</Link></div>;
  if (!d) return <div className="wrap space-y-4 py-16"><Skeleton className="h-12 w-1/2" /><Skeleton className="h-64 w-full" /></div>;
  const hasData = d.staff_count > 0 || d.manager_count > 0;

  return (
    <div className="wrap py-12" data-testid="team-dashboard">
      <p className="kicker">Team 360° dashboard · {d.team.code} · Round {d.round}</p>
      <h1 className="mt-2 text-4xl font-extrabold" data-testid="team-name">{d.team.name}</h1>
      <p className="mt-1 muted">{teamMeta(d.team)}</p>
      <div className="mt-8"><Invites code={d.team.code} admin={admin} /></div>
      <Rounds d={d} code={d.team.code} admin={admin} onChange={setRound} />
      <Trend history={d.history} />
      {d.round !== d.current_round && <p className="mt-4 rounded-lg bg-[#FFEEE5] p-3 text-sm" data-testid="past-round-note">You're viewing round {d.round}. New responses go to round {d.current_round}.</p>}
      <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Staff responses" value={d.staff_count} testid="stat-staff-count" />
        <Stat label="Staff average" value={d.staff_overall == null ? null : Math.round(d.staff_overall)} testid="stat-staff-overall" color={STAFF} />
        <Stat label="Manager self-score" value={d.manager_overall == null ? null : Math.round(d.manager_overall)} testid="stat-manager-overall" color={MANAGER} />
        <Stat label="Perception gap" value={d.overall_gap == null ? null : signed(d.overall_gap)} testid="stat-gap" color={gapTone(d.overall_gap)} />
      </section>
      <section className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="team-bandwidth">
        <Stat label="Avg mental bandwidth" value={d.bandwidth_avg == null ? null : Math.round(d.bandwidth_avg)} testid="stat-bandwidth" color={MANAGER} />
        <Stat label="Untapped capacity" value={d.untapped_count} testid="stat-untapped" color={STAFF} />
        <Stat label="Stretched" value={d.stretched_count} testid="stat-stretched" />
        <div className="panel p-5" data-testid="band-mix"><p className="kicker">Band mix</p>{Object.entries(d.band_counts || {}).map(([b, n]) => <p key={b} className="mt-1 flex justify-between text-sm"><span>{b}</span><b>{n}</b></p>)}</div>
      </section>
      {d.staff_count > 0 && d.staff_count < 3 && <p className="mt-3 text-sm text-[var(--orange)]">Fewer than 3 staff responses — averages may identify individuals.</p>}
      {hasData ? <><Charts d={d} /><Matrix d={d} /><Mix d={d} /></> : (
        <div className="panel mt-6 p-12 text-center" data-testid="team-empty"><p className="text-2xl font-bold">No responses yet</p><p className="mt-2 muted">Share the staff and manager links above. Results appear here automatically.</p></div>
      )}
      <Insights key={d.round} code={d.team.code} admin={admin} initial={d.insights} disabled={!d.staff_count} round={d.round} />
    </div>
  );
}
